import { Spinner, StatusBadge, type StatusBadgeTone } from '@immo/ui-kit';
import type { FleetCommandOutcome } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import type { FleetCommandStep } from './command-steps';

/**
 * A tone for every outcome token, not only the four the design's fixture
 * draws (BL-024) — a graceful `t()` fallback needs a totality check over the
 * set it serves, proven by `command-outcome-badge.test.tsx`.
 */
const OUTCOME_TONES: Record<Exclude<FleetCommandOutcome, 'accepted'>, StatusBadgeTone> = {
  started: 'success',
  'already-running': 'neutral',
  stopped: 'success',
  'already-stopped': 'neutral',
  synced: 'success',
  dirty: 'warning',
  'not-fast-forwardable': 'warning',
  cleaned: 'success',
  restarting: 'success',
  'unknown-profile': 'warning',
  refused: 'warning',
  failed: 'danger',
  'timed-out': 'danger',
  ok: 'success',
  'no-pane': 'warning',
  'log-absent': 'warning',
  reset: 'success',
  uploaded: 'success',
  'handed-over': 'success',
};

const STEP_LABELS = {
  waiting: 'accepted',
  sent: 'taken',
  received: 'received',
  running: 'running',
} as const satisfies Record<FleetCommandStep, string>;

type Props = {
  readonly outcome: FleetCommandOutcome;
  /** Where an unsettled command is (OPS-57); ignored once it has settled. */
  readonly step?: FleetCommandStep;
  /** How it ran out of time: `not-picked-up` or `no-result`. */
  readonly detail?: string | null;
};

/**
 * `accepted` is the only unsettled member — a dashed pill with a spinner,
 * never a `StatusBadge` (BL-064: pending must differ from settled by kind,
 * not by one ramp step). It names the step the command is at (OPS-57) —
 * waiting for its host, sent, received, running. They are all unsettled, and
 * they are not the same news.
 *
 * `timed-out` splits the same way, on the same fact — never picked up, versus
 * taken and then silence.
 */
/** The copy for where an unsettled command is — the pill's, and every pending spinner's. */
export function useCommandStepLabel(step: FleetCommandStep): string {
  const t = useTranslations('pages.admin.fleet.outcome');

  return t(STEP_LABELS[step]);
}

export function CommandOutcomeBadge({ outcome, step = 'waiting', detail = null }: Props) {
  const t = useTranslations('pages.admin.fleet.outcome');
  const stepLabel = useCommandStepLabel(step);

  if (outcome === 'accepted') {
    return (
      <span className="inline-flex h-5 items-center gap-1.5 whitespace-nowrap rounded-full border border-dashed border-neutral-300 px-2 text-sm font-medium text-text-placeholder">
        <Spinner size="sm" className="h-3 w-3" />
        {stepLabel}
      </span>
    );
  }

  if (outcome === 'timed-out') {
    return <StatusBadge label={t(detail === 'no-result' ? 'timed-out-no-result' : 'timed-out')} tone="danger" />;
  }

  return <StatusBadge label={t(outcome)} tone={OUTCOME_TONES[outcome]} />;
}
